import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsfTerminalsApiMeta } from "../apiMeta";
import {
  type TerminalBulletinsByIdInput,
  terminalBulletinsByIdInputSchema,
} from "./shared/terminalBulletins.input";
import {
  type TerminalBulletin,
  terminalBulletinSchema,
} from "./shared/terminalBulletins.output";

/**
 * Metadata for the fetchTerminalBulletinsByTerminalId endpoint
 */
export const terminalBulletinsByTerminalIdMeta = {
  functionName: "fetchTerminalBulletinsByTerminalId",
  endpoint: "/terminalBulletins/{TerminalID}",
  inputSchema: terminalBulletinsByIdInputSchema,
  outputSchema: terminalBulletinSchema,
  sampleParams: { TerminalID: 3 },
  endpointDescription:
    "Get bulletins and alerts for a specific terminal by ID.",
} satisfies EndpointMeta<TerminalBulletinsByIdInput, TerminalBulletin>;

/**
 * Fetch function for retrieving bulletins and alerts for a specific terminal by ID
 */
const { fetch, useHook } = createFetchAndHook<
  TerminalBulletinsByIdInput,
  TerminalBulletin
>({
  api: wsfTerminalsApiMeta,
  endpoint: terminalBulletinsByTerminalIdMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/terminalBulletins.endpoints").terminalBulletinsGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving bulletins and alerts for a specific terminal by ID
 */
export const fetchTerminalBulletinsByTerminalId: FetchFactory<
  TerminalBulletinsByIdInput,
  TerminalBulletin
> = fetch;

/**
 * React Query hook for retrieving bulletins and alerts for a specific terminal by ID
 */
export const useTerminalBulletinsByTerminalId: HookFactory<
  TerminalBulletinsByIdInput,
  TerminalBulletin
> = useHook;
